import { Component, OnInit, inject, DestroyRef } from '@angular/core'
import { Router, RouterModule, ActivatedRoute } from '@angular/router'
import { isPlatform, IonicModule, ToastOptions } from '@ionic/angular'
import { register } from 'swiper/element/bundle'
import { CommonModule } from '@angular/common'
import { SocketService } from './services/socket.service'
import { ToastService } from './services/toast.service'
import { distinctUntilChanged, first } from 'rxjs'
import { AuthService } from './services/auth.service'
import { environment } from '../environments/environment'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { combineLatest } from 'rxjs'
import { filter, startWith } from 'rxjs/operators'
import { NavigationEnd } from '@angular/router'
import { ExpiredPetsModalComponent } from './components/expired-pets-modal/expired-pets-modal.component'
import { CookieConsentPopupComponent } from './components/cookie-consent-popup/cookie-consent-popup.component'
import { CookieConsentService } from './services/cookie-consent.service'
import { H2HPet } from './types/pet'

// Add type declarations for the properties we add to XMLHttpRequest
declare global {
  interface XMLHttpRequest {
    _requestURL?: string
    _requestMethod?: string
  }
}

register()

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    RouterModule,
    ExpiredPetsModalComponent,
    CookieConsentPopupComponent,
  ],
})
export class AppComponent implements OnInit {
  isDesktop = isPlatform('desktop')
  socketService = inject(SocketService)
  toastService = inject(ToastService)
  authService = inject(AuthService)
  router = inject(Router)
  private route = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  private cookieConsentService = inject(CookieConsentService)

  isShelterPage = false
  shelterUuid: string | null = null

  // Expired pets modal state
  isExpiredPetsModalOpen = false
  expiredPets: H2HPet[] = []

  ngOnInit() {
    // Setup advanced error handling for network/CORS issues
    this.setupAdvancedErrorHandling()

    // Listen for expired pets check events from login flow
    this.setupExpiredPetsListener()

    // Re-trigger Google Translate widget on every completed navigation so
    // data-driven content that renders after route change (pet tables,
    // account messages, forms populated from API, etc.) gets translated.
    // The widget's own MutationObserver is unreliable for Angular's
    // change-detection re-renders; calling the exposed retranslate API
    // toggles the widget's combo to force a fresh full-DOM scan.
    // Debounced: we wait 1.2s after NavigationEnd so data-fetching
    // components have time to populate before the widget re-scans.
    let retranslateTimer: ReturnType<typeof setTimeout> | null = null
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (retranslateTimer) clearTimeout(retranslateTimer)
        retranslateTimer = setTimeout(() => {
          const api = (window as any).__h2hForceRetranslate
          if (typeof api === 'function') api()
        }, 1200)
      })

    // Track shelter UUID from query parameters and URL changes
    combineLatest([
      this.route.queryParams,
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, navigationEvent]) => {
        this.shelterUuid = params['shelterUuid'] || null

        // Check for expired pets popup trigger query parameter
        if (
          params['checkExpiredPets'] === 'true' &&
          this.authService.isAuthenticated$.value
        ) {
          this.checkForExpiredPets()
        }

        // Update isShelterPage based on both URL and query params
        const currentUrl = navigationEvent
          ? (navigationEvent as NavigationEnd).urlAfterRedirects
          : this.router.url
        this.updateShelterPageStatus(currentUrl, this.shelterUuid)
      })
  }

  constructor() {
    // Removed auto-consent on authentication to respect user cookie preferences
    // Users must make explicit cookie choices independent of login/registration

    // Init socket handler
    this.authService.isAuthenticated$.subscribe((isAuth) => {
      this.socketService.init()

      if (isAuth)
        this.socketService.latestMessage
          .pipe(distinctUntilChanged())
          .subscribe((data: any) => {
            if (data) {
              const { message, conversationUuid } = data
              if (
                message?.senderUuid?.includes('SYSTEM')
                // || message?.senderUuid != this.authService.getUuid()) && IS_NOT_MESSAGES_TAB
              ) {
                const toastConfig: ToastOptions = {
                  message: `${message.message}`,
                  duration: 8000,
                  color: 'success',
                }

                const { contractUuid } = message
                if (contractUuid) {
                  toastConfig.buttons = [
                    {
                      text: 'View Contract',
                      role: 'contract',
                      handler: () => {
                        this.router.navigate([`/contract/${contractUuid}`])
                      },
                      cssClass: ['btn', 'btn__primary'],
                    },
                  ]
                } else if (conversationUuid) {
                  toastConfig.buttons = [
                    {
                      text: 'View Message',
                      role: 'message',
                      handler: () => {
                        this.router.navigate(['/account/messages'])
                        // this.router.navigate([`/account/messages/${conversationUuid}`])
                      },
                      cssClass: ['btn', 'btn__primary'],
                    },
                  ]
                }

                this.toastService.presentToast(toastConfig)
              }
            }
          })
    })
  }

  /**
   * Setup advanced error handling for network/CORS errors
   * This monitors errors at the Angular application level
   */
  private setupAdvancedErrorHandling() {
    // Don't run this in production
    if (environment.production) {
      return
    }

    // Create a global error handler for misleading CORS errors
    window.addEventListener(
      'error',
      (event) => {
        const errorText = event.message || ''

        // Check if this is a CORS error
        if (
          errorText.includes('CORS') ||
          (event.filename &&
            event.filename.includes('https://api.home-home.org'))
        ) {
          // Prevent the default browser error handling
          event.preventDefault()

          // Log a clearer error message
          console.error(
            '%c🔍 Network Error Intercepted',
            'color: #3498db; font-weight: bold; font-size: 14px; background: #EFF8FF; padding: 4px 8px; border-radius: 4px;',
          )

          console.error(
            '%cA network error occurred that the browser reported as a CORS issue, but is likely a different problem.',
            'color: #333; font-size: 12px;',
          )

          console.error(
            '%cPossible causes:\n• API endpoint not responding\n• Network connectivity issues\n• Authentication failed\n• Server error\n\nCheck the Network tab in DevTools for more details.',
            'color: #333; background: #f8f8f8; padding: 8px; border-left: 4px solid #3498db; font-family: monospace; font-size: 12px;',
          )

          // Return true to indicate we've handled the error
          return true
        }

        // Let other errors be handled normally
        return false
      },
      true,
    )

    // Patch XMLHttpRequest to provide better error messages for CORS failures
    this.patchXMLHttpRequest()
  }

  /**
   * Patch XMLHttpRequest to provide better error messages when requests fail
   * This specifically targets issues that misleadingly appear as CORS errors
   */
  private patchXMLHttpRequest() {
    const originalOpen = XMLHttpRequest.prototype.open
    const originalSend = XMLHttpRequest.prototype.send

    // Override open to track request URLs
    XMLHttpRequest.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string | URL,
      async: boolean = true,
      username?: string | null,
      password?: string | null,
    ) {
      // Store URL to help with error reporting
      this._requestURL = url.toString()
      this._requestMethod = method

      // Call original method with proper arguments
      return originalOpen.call(
        this,
        method,
        url,
        async,
        username || null,
        password || null,
      )
    }

    // Override send to add custom error handling
    XMLHttpRequest.prototype.send = function (
      this: XMLHttpRequest,
      body?: Document | XMLHttpRequestBodyInit | null,
    ) {
      // Add an event listener for network errors
      this.addEventListener(
        'error',
        (event) => {
          // Only proceed if this request is to our API
          if (
            this._requestURL &&
            this._requestURL.includes('api.home-home.org') &&
            this._requestURL.includes('stripe/create-verification-session')
          ) {
            // If the browser will report this as CORS, provide better error
            console.error(
              '%c⚠️ API Request Failed: ' +
                this._requestMethod +
                ' ' +
                this._requestURL,
              'color: #e67e22; font-weight: bold; font-size: 13px;',
            )

            console.error(
              '%cThis appears to be an API connectivity issue, not a CORS error.\nThe server may be down or the endpoint may not exist.',
              'color: #333; font-size: 12px;',
            )

            // Prevent default browser error handling
            event.stopImmediatePropagation()
            event.stopPropagation()
            event.preventDefault()
          }
        },
        true,
      )

      // Call original method
      return originalSend.call(this, body)
    }
  }

  private updateShelterPageStatus(
    currentUrl: string,
    shelterUuid: string | null,
  ) {
    // isShelterPage is used for mobile menu styling and contact button visibility
    // User should see shelter-style mobile menu when:
    // 1. They're on a shelter page URL (/shelters/* or /shelter/* but not /shelters/info/*), OR
    // 2. They have a shelterUuid in query parameters
    const isShelterUrl =
      (currentUrl.includes('/shelters/') || currentUrl.includes('/shelter/')) &&
      !currentUrl.includes('/shelters/info/')
    const hasShelterUuid = shelterUuid !== null

    // Only apply this logic for mobile (same as original checkUrlForShelter logic)
    this.isShelterPage = !this.isDesktop && (isShelterUrl || hasShelterUuid)
  }

  /**
   * Setup listener for expired pets check events from login flow
   */
  private setupExpiredPetsListener() {
    window.addEventListener('checkExpiredPets', () => {
      this.checkForExpiredPets()
    })
  }

  /**
   * Check for expired pets and show popup if any exist
   */
  private checkForExpiredPets() {
    this.authService
      .checkForExpiredPets()
      .pipe(first())
      .subscribe({
        next: (expiredPets) => {
          if (expiredPets && expiredPets.length > 0) {
            this.showExpiredPetsModal(expiredPets)
          }
        },
        error: (error) => {
          console.error('Error checking for expired pets:', error)
          // Silently fail - don't show error to user for this background check
        },
      })
  }

  /**
   * Show styled modal for expired pets with individual actions
   */
  private showExpiredPetsModal(expiredPets: H2HPet[]) {
    this.expiredPets = expiredPets
    this.isExpiredPetsModalOpen = true
  }

  /**
   * Handle expired pets modal close
   */
  onExpiredPetsModalClosed() {
    this.isExpiredPetsModalOpen = false
    this.expiredPets = []
  }

  /**
   * Skip link handler (WCAG 2.4.1 Bypass Blocks).
   * Angular SPA fragments don't reliably move focus, so we explicitly focus
   * the <main> landmark. tabindex="-1" on <main> makes it programmatically
   * focusable without adding it to the natural tab order.
   */
  focusMainContent(event: Event) {
    event.preventDefault()
    const main = document.getElementById('main-content')
    if (main) {
      main.focus()
      // Also scroll to top so users land at the start of content
      main.scrollIntoView({ block: 'start' })
    }
  }

  /**
   * Build query parameters for adopt/foster links, including shelterUuid when available
   */
  buildFindPetQueryParams(baseParams: any): any {
    const params = { ...baseParams }

    // Add shelterUuid if we're on a shelter page
    if (this.shelterUuid) {
      params.shelterUuid = this.shelterUuid
    }

    return params
  }
}
